import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { DataTreeItem } from '../../services/api';
import { ListOptions } from '../../Views/OfflineView';
import OfflineLocSummary from './OfflineLocSummary';
import PartialOfflineLocation from './PartialOfflineLocation';
import { useAppSelector } from '../../state/store';
import { getCurrentLocation } from '../../state/selectors';

/**
 * Represents a single row for a Child Location.
 * Calculates its own sensor stats to keep the main component clean.
 */
interface LocationRowProps {
  locId: string;
  allSensors: DataTreeItem[];
  fullyOfflineLocs: Set<string>;
  listType: ListOptions;
  handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
}

// a sensor is treated as offline once its reported value drops to/below this threshold
const OFFLINE_VALUE_THRESHOLD = 0;

function LocationRow({
  locId,
  allSensors,
  fullyOfflineLocs,
  listType,
  handleTagging,
}: LocationRowProps) {
  const currentLocation = useAppSelector(getCurrentLocation);

  // Filter sensors specific to this location or its children
  const { locSensors, offlineSensors } = useMemo(() => {
    const isCurrentLocation = locId === currentLocation;

    // if isCurrentLocation, we only show a summary of offline sensors directly in this
    // location (not its sub-locations); otherwise include sub-locations too
    const relevantSensors = allSensors.filter(
      (item) =>
        item.location === locId || (!isCurrentLocation && item.location?.startsWith(`${locId}#`))
    );

    const offline = relevantSensors.filter(
      (item) => item.value !== undefined && item.value <= OFFLINE_VALUE_THRESHOLD
    );

    return {
      locSensors: relevantSensors,
      offlineSensors: offline,
    };
  }, [allSensors, locId, currentLocation]);

  const isFullyOffline = locSensors.length > 0 && locSensors.length === offlineSensors.length;
  const isPartiallyOffline = offlineSensors.length > 0 && !isFullyOffline;

  // nothing offline in this location - skip rendering an empty wrapper
  if (!isFullyOffline && !isPartiallyOffline) return null;

  return (
    <Box>
      {/* 1. Full Offline Summary */}
      {isFullyOffline && (
        <OfflineLocSummary
          locId={locId}
          listType={listType}
          locSensors={locSensors}
          offlineSensors={offlineSensors}
          handleTagging={handleTagging}
        />
      )}

      {/* 2. Partial Offline Details (Sub-locations) */}
      {isPartiallyOffline && (
        <PartialOfflineLocation
          offlineSensors={offlineSensors}
          locSensors={locSensors}
          excludeLocations={fullyOfflineLocs}
          listType={listType}
          handleTagging={handleTagging}
        />
      )}
    </Box>
  );
}

export default LocationRow;
